import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AdjustmentAccountOption,
    AdjustmentCandidate,
    AdjustmentEditEvent,
    AdjustmentRow,
    AdjustmentTransferMethod,
    AdjustmentTransferOptions,
} from '../../types/closing-adjustments.types';

/**
 * REV-21370 — the DETAIL surface of one correction booking (Pure View), AC8/AC9. Contract:
 * `e2e-lab/adjustment-detail.lab.spec.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * **Two jobs, and the second is the one the ticket grew** (ADR-0015 §4):
 *
 *   1. show one correction in full — every field, the source with the name of whoever entered or last
 *      edited it, the optional remark, and a way to the affected account. Editing, discarding and
 *      transferring are reachable here under **the same conditions as in the list** (AC6/AC7): there
 *      is no second rule, and the `editable` flag the backend serves is the only one either surface
 *      reads;
 *   2. resolve an AMBIGUOUS journal match. Where the reconciliation found SEVERAL lines matching all
 *      four fields, it guessed nothing — and this is where a person says which one it is.
 *
 * ⚠️ **The ambiguity picker is the ONLY control in this whole feature that leads to `Gebucht`, and it
 * does not break §2 — it confirms it.** The proof still comes from the journal; the user only names
 * WHICH of the lines the import contained is that proof. They cannot enter a line, cannot type an id,
 * and cannot choose one that was not among the candidates: the list is served from the record, and the
 * backend refuses anything outside it. `[value]` on the radio inputs is a candidate id from the
 * server, never free text — that is what makes "no invented proof" structural rather than a promise.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ **No agent surface** (§8): the source is shown as a provenance FACT — no confidence, no reasoning
 * trail, no proposal state, no accept and no reject. The lab spec asserts each absence.
 */

/** The three transfer targets, in the prototype's order — the same set as the list's menu (§3). */
const TRANSFER_TARGETS: readonly AdjustmentTransferMethod[] = ['erpApi', 'markedManual'];

const TRANSFER_TITLE_KEYS: Record<AdjustmentTransferMethod, string> = {
    erpApi: 'abcp_adjustments_transfer_erp',
    markedManual: 'abcp_adjustments_transfer_manual',
};

/** Rappen per franc. The amount travels in minor units everywhere; only this field shows majors. */
const MINOR_PER_MAJOR = 100;

@Component({
    selector: 'abcp-adjustment-detail',
    templateUrl: './adjustment-detail.component.html',
    styleUrls: ['./adjustment-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAdjustmentDetailComponent {
    /** `null` while loading or after an error — the view then renders the matching state. */
    @Input() adjustment: AdjustmentRow | null = null;

    /**
     * The candidate journal lines to choose from — non-empty exactly when the row is ambiguous (AC9).
     *
     * These are the lines THE RECONCILIATION FOUND, served from the record rather than recomputed: §4
     * allows a choice only among them, and that promise needs the set to be the one that was found.
     */
    @Input() candidates: AdjustmentCandidate[] = [];

    @Input() transferOptions: AdjustmentTransferOptions = {
        canWriteToErp: false,
        reasonKey: null,
        connectedName: null,
    };

    /** A prior year — everything is read-only, including the pick (§10). */
    @Input() readOnly = false;

    @Input() loading = false;
    @Input() error = false;

    /** A write is in flight. The controls stop accepting clicks rather than looking inert. */
    @Input() pending = false;

    /**
     * The mandate's chart of accounts — needed because `Soll` and `Haben` are edited IN PLACE here and
     * a picker cannot exist without its options.
     *
     * Empty leaves the two account rows read-only while everything else stays editable, rather than
     * offering a select with nothing in it: a picker with no options is a dead control that looks
     * live.
     */
    @Input() accounts: AdjustmentAccountOption[] = [];

    /**
     * A refusal from the last save, as a message KEY. Rendered above the buttons and cleared by the
     * host on the next attempt.
     */
    @Input() editErrorKey: string | null = null;

    /** Shows the `Zurück` button. `false` when the page header carries the back button. */
    @Input() showBack = true;

    @Output() backRequested = new EventEmitter<void>();
    /**
     * ⚠️ Kept for the surfaces that still open a DIALOG — the closing-wide list's row menu does.
     * This detail surface no longer uses it: editing happens in place, below.
     */
    @Output() editRequested = new EventEmitter<string>();

    /**
     * A finished in-place edit. The view reports the whole set of editable fields; the host writes.
     *
     * Every field travels on every save, including the untouched ones. The alternative — sending only
     * what changed — would make the host reconstruct the row to know what it is saving, and two
     * places would then hold an opinion about what the current values are.
     */
    @Output() editSubmitted = new EventEmitter<AdjustmentEditEvent>();

    /** `Verworfen` → `Freigegeben`. A report; the host performs the write. */
    @Output() restoreRequested = new EventEmitter<string>();
    @Output() discardRequested = new EventEmitter<string>();
    @Output() transferRequested = new EventEmitter<AdjustmentTransferMethod>();
    /** The account link — a navigation the host performs (AC8). */
    @Output() accountOpen = new EventEmitter<string>();
    /** The ambiguity pick. Carries a candidate id from the server, never free text (AC9). */
    @Output() ambiguityResolved = new EventEmitter<string>();
    @Output() retryRequested = new EventEmitter<void>();

    readonly transferTargets = TRANSFER_TARGETS;

    /** Which candidate the user has selected. Transient widget state — nothing upstream owns it. */
    selectedCandidateId: string | null = null;

    get isAmbiguous(): boolean {
        return this.candidates.length > 0;
    }

    /**
     * Whether editing and discarding are offered.
     *
     * ⚠️ `editable` comes from the BACKEND, where the rule lives (§7). The view does not derive it from
     * the state, because then two places would own one rule and only the backend's would be enforced.
     * A prior year is read-only on top of that (§10).
     */
    get canModify(): boolean {
        return !this.readOnly && !this.pending && (this.adjustment?.editable ?? false);
    }

    /** Transferring is offered under the same condition as in the list (AC7) — one rule, one place. */
    get canTransfer(): boolean {
        return this.canModify;
    }

    get canResolveAmbiguity(): boolean {
        return this.isAmbiguous && !this.readOnly && !this.pending && this.selectedCandidateId !== null;
    }

    /**
     * The source line, with the name of whoever entered or last edited it (AC8, §6).
     *
     * The EDITOR wins where there is one — the prototype shows the editor's name in place of the
     * source marking, and the ADR calls that out specifically: on a booking that has been corrected,
     * who last touched it is the more useful fact than where it originally came from.
     */
    get sourceLabelKey(): string {
        switch (this.adjustment?.source) {
            case 'agent':
                return 'abcp_adjustments_source_agent';
            case 'priorYear':
                return 'abcp_adjustments_source_prior_year';
            default:
                // `user` — and the default, because a booking with no source yet is one a person
                // entered. `AGENT` is the value that has to be stated explicitly.
                return 'abcp_adjustments_source_user';
        }
    }

    get sourceName(): string | null {
        const row = this.adjustment;
        if (row === undefined || row === null) {
            return null;
        }

        return row.editedBy ?? row.enteredBy ?? null;
    }

    transferTitleKey(method: AdjustmentTransferMethod): string {
        return TRANSFER_TITLE_KEYS[method];
    }

    /** `markedManual` is ALWAYS available — that is what it exists for (§3). */
    isTransferTargetEnabled(method: AdjustmentTransferMethod): boolean {
        return method !== 'erpApi' || this.transferOptions.canWriteToErp;
    }

    transferReasonKey(method: AdjustmentTransferMethod): string | null {
        return method === 'erpApi' && !this.transferOptions.canWriteToErp ? this.transferOptions.reasonKey : null;
    }

    trackByCandidate(_index: number, candidate: AdjustmentCandidate): string {
        return candidate.journalEntryId;
    }

    trackBySelf<T extends string | number>(_index: number, value: T): T {
        return value;
    }

    onBackClick(): void {
        this.backRequested.emit();
    }

    // ── In-place editing ────────────────────────────────────────────────────────────────────────
    //
    // ⚠️ **The layout does not change when editing starts.** Same rows, same order, same labels: each
    // value cell swaps its text for a control and nothing moves. A separate "edit view" would make the
    // user re-find the field they came for, and a dialog would hide the rest of the record behind the
    // one line being changed — which is the whole reason this surface exists.
    //
    // Draft state lives HERE, which is allowed and is the same licence the fiscal year has: it is
    // display state, not a decision. Nothing is computed from it, no status is derived, and the only
    // thing that leaves the component is the reported set of values.

    /** Whether the fields are currently controls rather than text. */
    isEditing = false;

    draftText = '';
    draftDebitAccountId = '';
    draftCreditAccountId = '';
    draftAmountInput = '';
    draftDate = '';
    draftRemark = '';

    /**
     * `Bearbeiten` — turns the fields into controls, seeded from the row on screen.
     *
     * Guarded by `canModify`, which is the BACKEND's `editable` answer and not a derivation here: a
     * transferred correction is immutable (§7), and the view must not be the place that decides so.
     */
    onEditClick(): void {
        if (!this.canModify || this.adjustment === null) {
            return;
        }

        const row = this.adjustment;
        this.draftText = row.text;
        this.draftDebitAccountId = row.debit.id;
        this.draftCreditAccountId = row.credit.id;
        this.draftAmountInput = (row.amount.minor / MINOR_PER_MAJOR).toFixed(2);
        this.draftDate = row.date;
        this.draftRemark = row.remark ?? '';
        this.isEditing = true;
    }

    /** `Abbrechen` — drops the draft. The row on screen was never touched, so nothing needs undoing. */
    onEditCancel(): void {
        this.isEditing = false;
    }

    /**
     * `Speichern` — reports the values. The view does not write and does not close itself.
     *
     * It stays in edit mode until the host says the write settled, which the host does by handing back
     * a changed `adjustment`. Closing optimistically would hide a refusal behind a read-only row that
     * still showed the old values.
     */
    onEditSave(): void {
        if (!this.canEditSave || this.adjustment === null) {
            return;
        }

        this.editSubmitted.emit({
            adjustmentId: this.adjustment.id,
            text: this.draftText.trim(),
            debitAccountId: this.draftDebitAccountId,
            creditAccountId: this.draftCreditAccountId,
            amountMinor: this.draftAmountMinor,
            date: this.draftDate,
            // The attribution is NOT editable here, so the row's own value travels back unchanged —
            // the same rule the dialog follows, and for the same reason: sending `null` would silently
            // move the correction to its debit account every time somebody fixed a typo.
            ownerAccountId: this.adjustment.owner?.id ?? null,
            remark: this.draftRemark.trim() === '' ? null : this.draftRemark.trim(),
        });
    }

    /** `Wieder freigeben` on a withdrawn correction. */
    onRestoreClick(): void {
        if (this.adjustment !== null) {
            this.restoreRequested.emit(this.adjustment.id);
        }
    }

    /** The draft amount in minor units. `0` when the input is not a number the field accepts. */
    get draftAmountMinor(): number {
        const normalised = this.draftAmountInput.replace(/'/g, '').replace(',', '.');
        const parsed = Number(normalised);

        return Number.isFinite(parsed) ? Math.round(parsed * MINOR_PER_MAJOR) : 0;
    }

    /**
     * Whether the draft may be saved.
     *
     * The same four rules the backend enforces, checked here only so the button can be inactive
     * instead of offering a save that would be refused. The backend still enforces them — this is a
     * courtesy, never the guard.
     */
    get canEditSave(): boolean {
        return (
            this.draftText.trim().length > 0 &&
            this.draftDebitAccountId !== '' &&
            this.draftCreditAccountId !== '' &&
            this.draftDebitAccountId !== this.draftCreditAccountId &&
            this.draftAmountMinor > 0 &&
            this.draftDate !== '' &&
            !this.pending
        );
    }

    /** The two account rows are editable only when there is a plan to pick from. */
    get canPickAccounts(): boolean {
        return this.accounts.length > 0;
    }

    /** Whether this correction was withdrawn — the row then offers `Wieder freigeben` instead. */
    get isDiscarded(): boolean {
        return this.adjustment?.discarded === true;
    }

    accountLabel(account: AdjustmentAccountOption): string {
        return `${account.number} ${account.name}`;
    }

    trackByAccountId(_index: number, account: AdjustmentAccountOption): string {
        return account.id;
    }

    onDiscardClick(): void {
        if (this.canModify && this.adjustment !== null) {
            this.discardRequested.emit(this.adjustment.id);
        }
    }

    onTransferClick(method: AdjustmentTransferMethod): void {
        if (this.canTransfer && this.isTransferTargetEnabled(method)) {
            this.transferRequested.emit(method);
        }
    }

    onAccountClick(): void {
        const account = this.adjustment?.owner ?? this.adjustment?.debit ?? null;
        if (account !== null && account !== undefined) {
            this.accountOpen.emit(account.id);
        }
    }

    onCandidateSelect(journalEntryId: string): void {
        this.selectedCandidateId = journalEntryId;
    }

    onResolveClick(): void {
        if (this.canResolveAmbiguity && this.selectedCandidateId !== null) {
            this.ambiguityResolved.emit(this.selectedCandidateId);
        }
    }

    onRetryClick(): void {
        this.retryRequested.emit();
    }
}
